import { useCallback, useEffect, useMemo, useState } from "react";
import { dismissServiceResourceNote } from "@/lib/service/client";
import { ServiceGateBanner } from "./ServiceGateBanner";
import { ServiceResourceAlertsPanel } from "./ServiceResourceAlertsPanel";
import { ServiceMaterialUsageRecipientsSection } from "./ServiceMaterialUsageRecipientsSection";
import { ServiceSetupCatalogView } from "./ServiceSetupCatalogView";
import { ServiceSetupDetailView } from "./ServiceSetupDetailView";
import { ServiceSetupFirstServiceWizard } from "./ServiceSetupFirstServiceWizard";
import { ServiceSetupSharedFleetView } from "./ServiceSetupSharedFleetView";
import { ghostBtnClass, primaryBtnClass } from "./serviceShared";
import { useServiceAnchorLayerGate } from "./useServiceAnchorLayerGate";
import { useServiceDefinitionsPanel } from "./useServiceDefinitionsPanel";
import { useServiceOfferingsCatalog } from "./useServiceOfferingsCatalog";
import { useServiceResourceNotesSummary } from "./useServiceResourceNotesSummary";
import { useServiceSetupDeepLink } from "./useServiceSetupDeepLink";

type SetupSurface = "catalog" | "detail" | "sharedFleet";

export function ServiceSetupPanel() {
  const gate = useServiceAnchorLayerGate();
  const [navTick, setNavTick] = useState(0);
  const catalog = useServiceOfferingsCatalog(navTick);
  const definitions = useServiceDefinitionsPanel(navTick);
  const notesSummary = useServiceResourceNotesSummary(navTick, definitions.allRigs, definitions.allTechs);
  const [surface, setSurface] = useState<SetupSurface>("catalog");
  const [selectedOfferingId, setSelectedOfferingId] = useState<string | null>(null);
  const [wizardActive, setWizardActive] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardOfferingId, setWizardOfferingId] = useState<string | null>(null);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [dismissBusy, setDismissBusy] = useState(false);

  const activeOfferings = useMemo(
    () => catalog.offerings.filter((o) => o.is_active),
    [catalog.offerings]
  );
  const showWizard = wizardActive && surface === "catalog";

  const handleRefresh = useCallback(() => {
    gate.refresh();
    setNavTick((s) => s + 1);
  }, [gate]);

  useEffect(() => {
    if (activeOfferings.length > 0) {
      setWizardActive(false);
      return;
    }
    if (surface === "catalog") {
      setWizardActive(true);
      if (!catalog.showForm) catalog.openCreateForm();
    }
  }, [activeOfferings.length, catalog.showForm, surface]);

  const finishWizard = useCallback(() => {
    setWizardActive(false);
    if (wizardOfferingId) {
      setSelectedOfferingId(wizardOfferingId);
      setSurface("detail");
      return;
    }
    handleRefresh();
  }, [handleRefresh, wizardOfferingId]);

  const handleDismissAlert = useCallback(
    async (noteId: string) => {
      setDismissBusy(true);
      await dismissServiceResourceNote(noteId);
      setDismissBusy(false);
      await notesSummary.reload();
      await definitions.resources.loadNotes();
    },
    [definitions.resources, notesSummary]
  );

  const openOffering = useCallback((offeringId: string) => {
    setSelectedOfferingId(offeringId);
    setSurface("detail");
  }, []);

  const allOfferings = useMemo(
    () => [...catalog.offerings, ...definitions.offerings],
    [catalog.offerings, definitions.offerings]
  );

  useServiceSetupDeepLink({
    offerings: allOfferings,
    onOpenOffering: openOffering,
    onClearWizard: () => setWizardActive(false),
  });

  const openCatalog = useCallback(() => {
    setSurface("catalog");
    setSelectedOfferingId(null);
    definitions.openGeneralScope();
  }, [definitions]);

  const alertCount = notesSummary.alertCount;

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="min-w-0 space-y-1">
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Service setup</h2>
          <p className="text-sm text-gray-600 dark:text-zinc-400">
            Configure each service end-to-end — basics, crew, quoting, and documents in one place.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={
              "inline-flex h-9 min-w-9 items-center justify-center rounded-lg border px-2 text-sm font-bold " +
              (alertCount > 0
                ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
                : "border-gray-200 bg-gray-50 text-gray-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-500")
            }
            title={
              alertCount > 0
                ? `${alertCount} resource alert${alertCount === 1 ? "" : "s"}`
                : "Resource alerts"
            }
            onClick={() => setAlertsOpen(true)}
            disabled={!gate.enabled}
          >
            !{alertCount > 0 ? ` ${alertCount}` : ""}
          </button>
          {surface === "detail" ? (
            <button
              type="button"
              className={primaryBtnClass}
              onClick={definitions.bumpNewResource}
              disabled={!gate.enabled}
            >
              + New resource
            </button>
          ) : null}
          <button
            type="button"
            className={ghostBtnClass}
            onClick={handleRefresh}
            disabled={gate.loading || catalog.listBusy || definitions.listBusy}
          >
            {catalog.listBusy || definitions.listBusy ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <ServiceResourceAlertsPanel
        open={alertsOpen}
        onClose={() => setAlertsOpen(false)}
        alerts={notesSummary.activeAlerts}
        onDismiss={(id) => void handleDismissAlert(id)}
        dismissBusy={dismissBusy}
      />

      <ServiceGateBanner gate={gate} />

      {gate.enabled && (catalog.localError || definitions.localError) ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {catalog.localError ?? definitions.localError}
        </div>
      ) : null}

      {gate.enabled ? (
        <>
          {surface === "sharedFleet" ? (
            <ServiceSetupSharedFleetView
              definitions={definitions}
              onBack={() => {
                definitions.openGeneralScope();
                setSurface("catalog");
              }}
            />
          ) : surface === "detail" && selectedOfferingId ? (
            <ServiceSetupDetailView
              offeringId={selectedOfferingId}
              catalog={catalog}
              definitions={definitions}
              navTick={navTick}
              onBack={openCatalog}
              onRefresh={handleRefresh}
            />
          ) : showWizard ? (
            <ServiceSetupFirstServiceWizard
              step={wizardStep}
              offeringId={wizardOfferingId}
              catalog={catalog}
              definitions={definitions}
              onStepChange={setWizardStep}
              onOfferingCreated={(id) => {
                setWizardOfferingId(id);
                handleRefresh();
              }}
              onFinish={finishWizard}
            />
          ) : (
            <ServiceSetupCatalogView
              catalog={catalog}
              definitions={definitions}
              navTick={navTick}
              onOpenOffering={openOffering}
              onOpenSharedFleet={() => {
                definitions.openGeneralScope();
                setSurface("sharedFleet");
              }}
              onCreateService={() => catalog.openCreateForm()}
              onRefresh={handleRefresh}
            />
          )}
          {surface === "catalog" ? <ServiceMaterialUsageRecipientsSection /> : null}
        </>
      ) : null}
    </div>
  );
}
